/**
 * When a deleted task can be restored, and what to say when it cannot.
 *
 * The rule is the backend's (`app/services/task_restore.py`): a deleted task
 * may be restored exactly when the same task could be created now. Only the
 * episode-status half is mirrored here, and it gates visits alone, because only
 * creating a visit requires an active episode. An order's rules (its
 * one-per-episode slot, its linked visit) depend on state this page does not
 * have, so they stay server-side and surface as a refusal toast. This
 * copy exists so the control can explain itself before the request rather than
 * offering an action that always fails; the backend remains the authority and
 * refuses the same case for the same reason, in its own wording.
 *
 * The status is a bare string on the wire (`TaskListResponse.episode_status`),
 * so there is no generated enum to key off; the literal below is the value the
 * backend's EpisodeStatus.ACTIVE serialises to.
 */

const ACTIVE_EPISODE_STATUS = 'active';

export const RESTORE_BLOCKED_BY_EPISODE =
  'This episode is not active, so this visit cannot be restored.';

export interface RestorableTask {
  task_type: string;
  episode_status?: string | null;
}

/**
 * The reason this task cannot be restored, or null when it can be.
 *
 * A missing `episode_status` counts as not active: the control is disabled with
 * a reason rather than offered on an answer we do not have.
 */
export function taskRestoreBlockedReason(task: RestorableTask): string | null {
  if (task.task_type !== 'visit') return null;
  return task.episode_status === ACTIVE_EPISODE_STATUS
    ? null
    : RESTORE_BLOCKED_BY_EPISODE;
}
